<template>
  <div class="flex h-full p-1">
    <div v-if="current_vue === view.Actions" class="flex flex-1 flex-col gap-2">
      <div class="flex flex-1 flex-col gap-3">
        <div>
          <BaseShowEditSwitch
            v-model="localPrintSettings.electronic_signature"
            name="electronic_signature"
            :label="$t('attributes.electronic_signature')"
            :edition-mode="true"
            @change="onSignatureChange"
          >
            <div
              v-if="localPrintSettings.electronic_signature"
              class="px-2.5 pb-1.5"
            >
              <BaseShowEditSwitch
                v-if="document?.settings?.electronic_signature?.advisor"
                v-model="localPrintSettings.electronic_signature_advisor"
                name="electronic_signature_advisor"
                :label="
                  $t(`attributes.signature`) +
                  ' ' +
                  $t(`attributes.from`) +
                  ' ' +
                  $t(`attributes.agent`)
                "
                :edition-mode="true"
              />
            </div>
          </BaseShowEditSwitch>
        </div>
        <div>
          <button
            data-tint="primary"
            class="bg-secondary-300 capitalize-first relative block w-full overflow-hidden rounded-md p-2 text-left font-semibold"
            @click="current_vue = 'form'"
          >
            {{ $t('button.change_layout_options') }}
            <FontAwesomeIcon
              class="absolute -right-0.5 -bottom-0.5 opacity-25"
              size="2x"
              icon="cogs"
            />
          </button>
        </div>
        <div v-if="headers.length">
          <button
            data-tint="primary"
            class="bg-secondary-300 capitalize-first relative block w-full overflow-hidden rounded-md p-2 text-left font-semibold"
            @click="current_vue = 'header'"
          >
            {{ $t('button.change_the_letterhead') }}
            <FontAwesomeIcon
              class="absolute -right-0.5 -bottom-0.5 opacity-25"
              size="2x"
              :icon="['fal', 'file-invoice']"
            />
          </button>
        </div>
        <div v-if="$functionality.module.has('electronic_signature')">
          <button
            data-tint="primary"
            class="bg-secondary-300 capitalize-first relative block w-full overflow-hidden rounded-md p-2 text-left font-semibold"
            @click="onSignatureToggle"
          >
            {{
              localPrintSettings.electronic_signature
                ? $t('button.disable_electronic_signature')
                : $t('button.enable_electronic_signature')
            }}
            <FontAwesomeIcon
              class="absolute -right-0.5 -bottom-0.5 opacity-25"
              size="2x"
              :icon="['fal', 'signature']"
            />
          </button>
        </div>
        <div
          v-for="(innerMenuOption, idx) in menuOptions"
          :key="idx"
          class="grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(10rem,_1fr))] gap-3"
        >
          <button
            v-for="(menuOption, idx2) in innerMenuOption.filter(
              (option) => option.gate
            )"
            :key="idx2"
            data-tint="primary"
            class="bg-primary-300 capitalize-first relative overflow-hidden rounded-md p-2 text-left font-semibold"
            @click="menuOption.action"
          >
            <div class="truncate">{{ menuOption.label }}</div>
            <FontAwesomeIcon
              class="absolute -right-0.5 -bottom-0.5 opacity-25"
              size="2x"
              :icon="menuOption.icon"
            />
          </button>
        </div>
        <div
          v-if="
            invoice &&
            $functionality.module.has('peppol') &&
            [DocTypes.INVOICE, DocTypes.CREDIT_NOTE].includes(
              invoice.doc_type
            ) &&
            (invoice.invoiceable_normalized?.is_company ?? false)
          "
        >
          <PeppolSendInvoiceComponent
            :invoice="invoice"
            :print-settings="localPrintSettings"
            @refresh-invoice="peppolRefresh"
          />
        </div>
      </div>
    </div>
    <div
      v-else-if="[view.Form, view.Header].includes(current_vue)"
      class="flex flex-1 flex-col gap-4"
    >
      <div class="flex min-h-0 flex-1">
        <div class="scrollbar-custom flex flex-1 flex-col gap-6 overflow-auto">
          <template v-if="current_vue === view.Form">
            <div class="flex flex-row items-center gap-x-2">
              <span class="shrink-0">
                <BaseButton
                  icon="chevron-left"
                  size="extra-small"
                  @click="current_vue = 'actions'"
                />
              </span>
              <h2 class="capitalize-first flex-1 text-xl font-bold">
                {{ $t(`enums.invoicing.docTypes.${invoice.doc_type}`) }}
              </h2>
            </div>
            <InvoicingPrintLeftMenuSection>
              <TheInvoicingPrintInvoiceForm
                v-model:print-settings="localPrintSettings"
                :covering-letters="coveringLetters"
                :footers="footers"
              />
            </InvoicingPrintLeftMenuSection>

            <InvoicingPrintLeftMenuSection :label="$t('global.options')">
              <TheInvoicingPrintOptionsForm
                v-model:print-settings="localPrintSettings"
              />
            </InvoicingPrintLeftMenuSection>
          </template>
          <template v-else-if="current_vue === view.Header">
            <HeaderSelector
              v-model:header="localPrintSettings.header"
              v-model:current-view="current_vue"
              :header-list="headers"
              @change="current_vue = view.Actions"
            />
          </template>
        </div>
      </div>
      <div class="flex shrink-0 flex-row justify-between">
        <BaseButton
          v-if="current_vue === view.Form"
          :icon="'file-invoice'"
          :title="$t('attributes.header')"
          size="small"
          color="light"
          @click="current_vue = view.Header"
        />
        <BaseButton
          v-if="current_vue === view.Header"
          class-name="bg-white"
          text-color="txt-gray-700"
          :icon="'list'"
          size="small"
          @click="current_vue = view.Form"
        />
        <BaseButton
          v-if="params_changed"
          theme-color="bg-secondary text-contrast-secondary"
          :title="$t('global.refresh_document')"
          icon="arrows-rotate"
          size="small"
          @click="() => handlerRefreshDocument()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import InvoicingPrintLeftMenuSection from '@/views/invoicing/show/print/components/leftMenu/components/InvoicingPrintLeftMenuSection'
import TheInvoicingPrintInvoiceForm from '@/views/invoicing/show/print/components/leftMenu/components/TheInvoicingPrintInvoiceForm'
import TheInvoicingPrintOptionsForm from '@/views/invoicing/show/print/components/leftMenu/components/TheInvoicingPrintOptionsForm'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import HeaderSelector from '@c/Media/HeaderSelector.vue'
import eventType from '@/assets/enums/events/EventType'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import PeppolSendInvoiceComponent from '@c/Partner/Peppol/PeppolSendInvoiceComponent.vue'
import DocTypes from '@/assets/enums/invoicing/DocTypes'
import BaseShowEditSwitch from '@c/BaseShowEditSwitch.vue'

export default {
  name: 'TheInvoicingPrintLeftMenu',
  components: {
    BaseShowEditSwitch,
    PeppolSendInvoiceComponent,
    FontAwesomeIcon,
    HeaderSelector,
    BaseButton,
    TheInvoicingPrintOptionsForm,
    TheInvoicingPrintInvoiceForm,
    InvoicingPrintLeftMenuSection
  },
  props: {
    printSettings: {
      type: Object,
      required: true
    },
    invoice: {
      type: Object,
      required: true
    },
    headers: {
      type: Array,
      required: false,
      default: () => []
    },
    coveringLetters: {
      type: Array,
      required: false,
      default: () => []
    },
    footers: {
      type: Array,
      required: false,
      default: () => []
    },
    menuOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    currentView: {
      type: String,
      default: 'actions'
    }
  },
  emits: [
    'refresh-document',
    'update:printSettings',
    'update:currentView',
    'refresh-invoice'
  ],
  data() {
    return {
      document_header: null,
      view: {
        Form: 'form',
        Header: 'header',
        Actions: 'actions'
      },
      current_vue: this.currentView,
      params_changed: false
    }
  },
  computed: {
    DocTypes() {
      return DocTypes
    },
    eventType() {
      return eventType
    },
    localPrintSettings: {
      get() {
        return this.printSettings
      },
      set(value) {
        this.$emit('update:printSettings', value)
      }
    }
  },
  watch: {
    printSettings: {
      handler(newValue, oldValue) {
        this.params_changed = true
      },
      deep: true
    },
    current_vue(newVal) {
      this.$emit('update:currentView', newVal)
    },
    currentView(newVal) {
      this.current_vue = newVal
      this.$emit('update:currentView', newVal)
    }
  },
  mounted() {
    // this.current_vue = this.view.Form
  },
  methods: {
    onSignatureChange(value) {
      if (value) {
        this.localPrintSettings.recto_verso = false
      }
      this.handlerRefreshDocument()
    },
    onSignatureToggle() {
      const newValue = !this.localPrintSettings.electronic_signature
      this.localPrintSettings.electronic_signature = newValue
      if (newValue) {
        this.localPrintSettings.recto_verso = false
      }
      this.handlerRefreshDocument()
    },
    handlerRefreshDocument() {
      this.params_changed = false
      this.$emit('refresh-document')
    },
    peppolRefresh() {
      this.$emit('refresh-document')
      this.$emit('refresh-invoice')
    }
  }
}
</script>
